// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { DxWidgetUtils, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';

// service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { WorkflowEditorService } from '../../_services/workflow-editor.service';

// model imports
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { WorkFlowParametersDto } from '../../../shared-workflow-module/_models/workflow-parameters-dto';
import { WFEDeleteWorkflowParametersDto } from '../../_models/common/wfe-delete-workflow-parameters-dto';
import { StatusMessageResultDto } from '../../../_models/status-message-result-dto';
import { WFEBaseParametersDto } from '../../../shared-workflow-module/_models/wfe-base-parameter-dto';

@Component({
    selector: 'app-workflow-community-attribution',
    templateUrl: './workflow-community-attribution.component.html',
    styleUrls: ['./workflow-community-attribution.component.scss']
})
export class WorkflowCommunityAttributionComponent implements OnInit {
    @ViewChild('workflowCommunityAttributionSelector', { static: false })
    workflowCommunityAttributionSelector: DxPopupComponent;
    @ViewChild('workflowAttributionMessageBox', { static: false }) workflowAttributionMessageBox: MessageBoxComponent;

    @Input() workflowId: number;
    @Input() workflowStatus: string;

    @Output() onClose = new EventEmitter<boolean>();
    @Output() onCommunityAttribution = new EventEmitter<number>();

    visible = false;
    loadingVisible = false;

    iconSave = IconConstants.SAVE;
    iconCancel = IconConstants.CANCEL;
    iconRandom = IconConstants.RANDOM;
    iconDelete = IconConstants.DELETE;

    communityColumns: Array<Column> = [];
    communityGridSource: Array<CommunitySelectorResultDto> = [];
    communityBoxValue: Array<number> = [];

    pdfURL: string;

    selectedCommunityId: number;
    showDeleteWorkflowPopUp = false;

    constructor(
        private readonly communityService: CommunityService,
        private readonly errorService: ServerErrorService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly helperService: HelperService,
        private readonly workflowEditorService: WorkflowEditorService
    ) {}

    ngOnInit() {
        this.visible = true;
        this.populateCommunities();
        this.getInvoice();
    }

    onPopupShown(): void {
        DxWidgetUtils.instantRepaint(this.workflowCommunityAttributionSelector);
    }

    onPopupClose(): void {
        this.closeAttributionSelector(false);
    }

    onCommunitySelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedRow: CommunitySelectorResultDto = selectedObject.selectedRow[0];
            this.selectedCommunityId = selectedRow.communityId;
        } else {
            this.selectedCommunityId = null;
        }
    }

    onCommunitySaveClick(): void {
        this.onCommunityAttribution.emit(this.selectedCommunityId);
        this.closeAttributionSelector(true);
    }

    onTransferToCorpWorkQueueClick(): void {
        this.transferWorkflowToCorpQueue();
    }

    onDeleteWorkflowClick(): void {
        this.showDeleteWorkflowPopUp = true;
    }

    onDeleteWorkflow(reason: string): void {
        this.deleteWorkflow(reason);
    }

    onDeleteWorkflowClose(): void {
        this.showDeleteWorkflowPopUp = false;
    }

    private transferWorkflowToCorpQueue(): void {
        const customDialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            `<div class="confirmation-message-container"><i class="fas fa-question-circle pop-up-icon"></i>Are you sure you want to transfer ${this.workflowId}<br/> to Corp Work Queue ?</div>`,
            'Yes',
            'No',
            'success',
            'default'
        );
        customDialog.show().then((answer) => {
            if (Utils.isTrue(answer)) {
                this.transferWorkQueue();
            }
        });
    }

    private transferWorkQueue(): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.workflowStatus = this.workflowStatus;

        this.loadingVisible = true;
        this.workflowEditorService.transferWorkQueue(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                if (Utils.isTrue(result.isSuccessMessage)) {
                    this.workflowAttributionMessageBox.showSuccessMessage('Transfer Work Queue', result.message, () => {
                        this.closeAttributionSelector(true);
                    });
                } else {
                    this.workflowAttributionMessageBox.showErrorMessage('Error', result.message);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private deleteWorkflow(deleteReason: string): void {
        const baseParams: WFEDeleteWorkflowParametersDto = new WFEDeleteWorkflowParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.workflowStatus = this.workflowStatus;
        baseParams.deleteReason = deleteReason;

        this.loadingVisible = true;
        this.workflowEditorService.deleteWorkflow(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                if (Utils.isTrue(result.isSuccessMessage)) {
                    this.workflowAttributionMessageBox.showSuccessMessage('Delete Workflow', result.message, () => {
                        this.closeAttributionSelector(true);
                    });
                } else {
                    this.workflowAttributionMessageBox.showErrorMessage('Error', result.message);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private getInvoice(): void {
        const workFlowParams = new WorkFlowParametersDto();
        workFlowParams.workflowId = this.workflowId;

        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(workFlowParams).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                this.pdfURL = fileURL;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateCommunities(): void {
        this.communityColumns = this.communityService.getCommunitySelectorColumns();

        this.loadingVisible = true;
        this.communityService.getCommunities(false, true).subscribe(
            (result: CommunitySelectorResultDto[]) => {
                this.communityGridSource = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private closeAttributionSelector(forceRefresh: boolean): void {
        this.visible = false;
        this.onClose.emit(forceRefresh);
    }
}
